class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // The header cart icon is a real link as a no-JS fallback, but the store
    // uses a drawer. Handle ALL cart-link clicks at document capture level:
    // header icons (including AJAX replacements) and ordinary /cart links.
    // Do not intercept checkout buttons, cart form submissions or links from
    // inside the drawer. Keep Shopify's original drawer implementation.
    const initialCartIcon = document.querySelector('#cart-icon-bubble');
    if (initialCartIcon) {
      initialCartIcon.setAttribute('role', 'button');
      initialCartIcon.setAttribute('aria-haspopup', 'dialog');
    }

    const normalizePath = (path) => path.replace(/\/+$/, '') || '/';

    const findCartTrigger = (target) => {
      const link = target?.closest?.('a[href]');
      if (!link || link.closest('cart-drawer') || link.closest('form')) return null;
      if (link.target && link.target !== '_self') return null;

      // Explicit header trigger, plus other links to Shopify's cart URL.
      if (link.matches('#cart-icon-bubble, .header__icon--cart')) return link;

      try {
        const clickedURL = new URL(link.href, window.location.href);
        const configuredCartURL = new URL(window.routes?.cart_url || '/cart', window.location.href);
        const path = normalizePath(clickedURL.pathname);
        const expected = normalizePath(configuredCartURL.pathname);
        if (clickedURL.origin !== configuredCartURL.origin) return null;
        if (path === expected) return link;
        // A store's translated route may use /ar/cart; both cart paths are
        // navigation to the same Shopify cart, not two different cart UIs.
        if (path === '/cart' || /^\/[a-z]{2}(?:-[a-z]{2})?\/cart$/i.test(path)) return link;
      } catch (error) {
        return null;
      }
      return null;
    };

    document.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const cartLink = findCartTrigger(event.target);
      if (!cartLink) return;

      event.preventDefault();
      // Stop any competing theme/app click handler from navigating to /cart.
      // Capturing makes this work even when the header cart icon is replaced.
      event.stopImmediatePropagation();
      cartLink.setAttribute('aria-haspopup', 'dialog');
      this.open(cartLink);
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || (event.code !== 'Space' && event.code !== 'Spacebar')) return;
      const cartLink = findCartTrigger(event.target);
      if (!cartLink || !cartLink.matches('#cart-icon-bubble, .header__icon--cart')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(cartLink);
    }, true);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Here the animation does not always appear to trigger. A timeout helps.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) {
      console.error('Cart drawer could not render because section HTML was not returned.');
      return;
    }

    // A successful add means the drawer is no longer empty. Remove stale empty
    // state from the elements that actually receive the class in the Liquid markup.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];

      if (!sectionElement || !sectionHtml) return;

      const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHtml === null) return;

      sectionElement.innerHTML = innerHtml;
    });

    // The drawer markup has now been replaced. Clear any stale empty state again
    // so all add-to-cart entry points behave consistently.
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

      if (!this.classList.contains('active')) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const sectionElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (mainCartFooter?.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
